/* eslint-disable max-lines-per-function */
import { useRef, useState, useEffect, useContext } from 'react';
import lottie from 'lottie-web';
import { Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Filters from '../Filters/filters';
import CategoryList from '../CategoryList/categoryList';
import { MANAGE_KPI_DASHBOARD, SAVE, SAVE_SUCCESS } from '../../../Constants/strings';
import styles from './setKpi.module.scss';
import { AuthContext } from '../../../Contexts/AuthContext';
import { addKpi } from './setKpiUtil';
import { useSnackbar } from '../../../Components/Snackbar/snackBarProvider';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchPreviousKpiData } from './kpiPerformanceCycleUtil';

const SetKpi = () => {
  const [verticalSelected, setVerticalSelected] = useState([]);
  const [departmentSelected, setDepartmentSelected] = useState([]);
  const [designationSelected, setDesignationSelected] = useState([]);
  const [performancePeriodSelected, setPerformancePeriodSelected] = useState([]);
  const [kpiDetails, setKpiDetails] = useState({});
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const saveBtnRef = useRef();
  const { userProfile } = useContext(AuthContext);
  const { snackbarOpen } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const { showImage } = location.state || {};
  const [previousKpiDetails, setPreviousKpiDetails] = useState([]);
  const [previousPerformanceSelected, setPreviousPerformanceSelected] = useState([]);
  const [noDataMessage, setNoDataMessage] = useState('');
  const [animationData, setAnimationData] = useState(null);
  const animationContainer = useRef(null);

  const onVerticalChangeHandler = (val) => {
    setVerticalSelected(val);
    localStorage.setItem('kpiVertical', JSON.stringify(val));
  };
  const onDepartmentChangeHandler = (val) => {
    setDepartmentSelected(val);
    localStorage.setItem(
      'kpiDepartment',
      JSON.stringify(val),
    );
  };
  const onDesignationChangeHandler = (val) => {
    setDesignationSelected(val);
    localStorage.setItem(
      'kpiDesignation',
      JSON.stringify(val),
    );
  };

  useEffect(() => {
    const storedKpiPerformance = localStorage.getItem('kpiPerformance');

    if (storedKpiPerformance) {
      const parsedKpiPerformance = JSON.parse(storedKpiPerformance);
      setPerformancePeriodSelected([parsedKpiPerformance]);
    }

    const storedKpiPreviousPerformance = localStorage.getItem('kpiPreviousPerformance');

    if (storedKpiPreviousPerformance) {
      const parsedKpiPreviousPerformance = JSON.parse(storedKpiPreviousPerformance);
      setPreviousPerformanceSelected([parsedKpiPreviousPerformance]);
    }
  }, []);

  const onKpiDetailsChange = ({ id, kpis }) => {
    setKpiDetails((prevKpiDetails) => ({
      ...prevKpiDetails,
      [id]: kpis,
    }));
  };

  useEffect(() => {
    const isAllSelected = verticalSelected?.id && departmentSelected?.id
    && designationSelected?.id && performancePeriodSelected[0]?.id;

    if (isAllSelected) {
      setIsSaveEnabled(true);
    } else {
      setIsSaveEnabled(false);
    }
  }, [verticalSelected, departmentSelected, designationSelected, performancePeriodSelected]);

  useEffect(() => {
    let kpiDetailsArray = [];
    let isValid = false;

    for (const category in kpiDetails) {
      kpiDetailsArray.push(kpiDetails[category]);
    }

    kpiDetailsArray = kpiDetailsArray.flat();

    kpiDetailsArray.forEach((details) => {
      if (details?.symbolTargetValue || details?.targetValue) {
        isValid = true;
      }
    });
    const isAllSelected
      = verticalSelected?.id && departmentSelected?.id && designationSelected?.id && performancePeriodSelected[0]?.id;

    if (saveBtnRef?.current) {
      if (isValid && isAllSelected && kpiDetailsArray.length) {
        saveBtnRef.current.disabled = false;
        saveBtnRef.current.className = styles['save-btn'];
      } else {
        saveBtnRef.current.disabled = true;
        saveBtnRef.current.className = `${styles['save-btn']} ${styles.disabled}`;
      }
    }
  }, [kpiDetails, verticalSelected, departmentSelected, designationSelected, performancePeriodSelected]);

  const validateKpiDetails = async (kpiDetailsArray) => {
    for (const details of kpiDetailsArray) {
      if (!details?.symbolTargetValue && !details?.targetValue) {
        return true;
      }
    }
  };

  const processTargetFormatOperatorId = (item) => {
    if (typeof item.targetFormatId === 'object') {
      if (item.targetFormatId.format === 'Yes/No') {
        if (typeof item.targetValue === 'object') {
          return item.targetValue.kpiTargetFormatId;
        }
        return '';
      } else if (item.symbol) {
        const matchingOption = item.targetFormatId.options.find(
          (opt) => opt.combination === item.symbol.combination || opt.combination === item.symbol,
        );
        return matchingOption ? matchingOption.kpiTargetFormatId : '';
      }
    } else if (typeof item.symbol === 'object') {
      return item.symbol.kpiTargetFormatId;
    } else if (typeof item.targetValue === 'object') {
      return item.targetValue.kpiTargetFormatId;
    }
    return item.kpiFormatOpertorId || '';
  };

  const buildKpiDataObject = (item) => {
    const targetValue = item.symbolTargetValue || '';
    const targetFormatOperatorId = processTargetFormatOperatorId(item);

    return {
      kpiId: item?.kpiId?.id,
      targetFormatOperatorId: targetFormatOperatorId,
      targetValue: targetValue,
    };
  };

  useEffect(() => {
    fetchPreviousKpiData({
      verticalSelected,
      departmentSelected,
      designationSelected,
      previousPerformanceSelected,
      showImage,
      isSaveEnabled,
      setPreviousKpiDetails,
      setNoDataMessage,
    });

    if (previousKpiDetails.length > 0) {
      navigate('/configuration/manage-kpi/edit-kpi', { state: { kpiDetails: previousKpiDetails, showImage } });
    } else if (noDataMessage) {
      snackbarOpen(noDataMessage, 'warning');
    }
  }, [isSaveEnabled, previousKpiDetails, noDataMessage, verticalSelected, showImage,
    departmentSelected, designationSelected, previousPerformanceSelected]);

  const getKPIDetails = async () => {
    let kpiDetailsArray = [];
    for (const category in kpiDetails) {
      kpiDetailsArray.push(kpiDetails[category]);
    }

    kpiDetailsArray = kpiDetailsArray.flat();

    const failure = await validateKpiDetails(kpiDetailsArray);

    if (failure) {
      snackbarOpen(MANAGE_KPI_DASHBOARD.SET.FAILURE_REQUIRED_TARGET_VALUES, 'warning');
      return;
    }

    const kpiData = kpiDetailsArray.map(buildKpiDataObject);

    submitData(kpiData);
  };
  const submitData = async (kpiInfo) => {
    const data = {
      verticalId: verticalSelected?.id,
      departmentId: departmentSelected?.id,
      designationId: designationSelected?.id,
      performanceCycleId: performancePeriodSelected[0]?.id,
      kpiDetails: kpiInfo,
      lastUpdatedBy: userProfile.user_name,
    };

    const api = await addKpi(data);
    if (api.failure) {
      snackbarOpen(api.failure, 'warning');
    } else {
      snackbarOpen(SAVE_SUCCESS, 'success');
      navigate('/configuration/manage-kpi');
    }
  };

  useEffect(() => {
    import('../../../Assets/images/json/dar_ticker.json').then(
      ({ default: defAnimationData }) => {
        setAnimationData(defAnimationData);
      },
    );
  }, []);

  useEffect(() => {
    if (animationData) {
      lottie.loadAnimation({
        container: animationContainer.current,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData,
      });
    }
  }, [animationData]);

  return (
    <div className={styles.container}>
      <Filters
        verticalSelected={verticalSelected}
        setVertical={onVerticalChangeHandler}
        departmentSelected={departmentSelected}
        setDepartment={onDepartmentChangeHandler}
        designationSelected={designationSelected}
        setDesignation={onDesignationChangeHandler}
      />
      {showImage ? (
        <div className={styles['ticker-animation-container']}>
          <div className={styles['ticker-animation-contents']}>
            <div ref={animationContainer} className={styles['ticker-animation-image']}></div>
            <Typography className={styles['ticker-animation-text']}>
              {MANAGE_KPI_DASHBOARD.SELECT_VERTICAL_DEPT_DESG}
            </Typography>
          </div>
        </div>
      ) : (
        <CategoryList updateKpiDetails={onKpiDetailsChange} />
      )}
      <div className={styles['btn-container']}>
        <Button variant="text" onClick={getKPIDetails} ref={saveBtnRef}>
          {SAVE}
        </Button>
      </div>
    </div>
  );
};

export default SetKpi;
